import { createAsyncThunk } from '@reduxjs/toolkit'
import axiosInstance from 'api/apiCommon'
import { type PaginatedResponse } from 'common/utils/types'
import { VendorStatus } from 'features/vendor/vendor.types'

import axios from '../../api/apiCommon'

import {
  type AIResult,
  type ListAIVendor,
  type ListVendor,
  type RespondToAIVerificationPayload,
} from './vendorList.types'

export interface GetVendorListPayload {
  ipp: number
  page: number
  search: string
}

async function getVendorList(
  ipp: number,
  page: number,
  status: VendorStatus,
  search: string,
): Promise<{
  page: number
  list: PaginatedResponse<ListVendor[]>
}> {
  return await new Promise((resolve, reject) => {
    const searchString = search ? `&search=${search}` : ''
    axiosInstance
      .get<PaginatedResponse<ListVendor[]>>(
        `/vendors?status=${status}&ipp=${ipp}&page=${page}${searchString}`,
      )
      .then((data) => {
        resolve({ page, list: data })
      })
      .catch((err) => {
        console.error(err)
        reject(err)
      })
  })
}

export const getVerifiedVendors = createAsyncThunk(
  'vendor/verifiedVendorList',
  async ({
    ipp,
    page,
    search,
  }: GetVendorListPayload): Promise<{
    page: number
    list: PaginatedResponse<ListVendor[]>
  }> => {
    return await getVendorList(ipp, page, VendorStatus.verified, search)
  },
)

export const getRejectedVendors = createAsyncThunk(
  'vendor/rejectedVendorList',
  async ({
    ipp,
    page,
    search,
  }: GetVendorListPayload): Promise<{
    page: number
    list: PaginatedResponse<ListVendor[]>
  }> => {
    return await getVendorList(ipp, page, VendorStatus.rejected, search)
  },
)

export const getListOfAIVendors = createAsyncThunk(
  'vendor/getListOfAIVendors',
  async ({
    ipp,
    page,
    search,
  }: GetVendorListPayload): Promise<{
    page: number
    list: PaginatedResponse<ListAIVendor[]>
  }> => {
    return await new Promise((resolve, reject) => {
      const searchString = search ? `&search=${search}` : ''
      axiosInstance
        .get<PaginatedResponse<ListAIVendor[]>>(`/list-ai-vendors?&ipp=${ipp}&page=${page}${searchString}`)
        .then((data) => {
          resolve({ page, list: data })
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  },
)

export const getPendingVendors = createAsyncThunk(
  'vendor/pendingVendorList',
  async ({
    ipp,
    page,
    search,
  }: GetVendorListPayload): Promise<{
    page: number
    list: PaginatedResponse<ListVendor[]>
  }> => {
    return await getVendorList(ipp, page, VendorStatus.pendingVerification, search)
  },
)

export const respondToAIVerification = createAsyncThunk(
  'vendor/vendorRespondAIVerification',
  async ({
    vendorId,
    aiStatus,
    aiVerificationNote,
    aiVerificationRetryDate,
  }: RespondToAIVerificationPayload): Promise<{ vendorId: string; aiStatus: string }> => {
    return await new Promise((resolve, reject) => {
      axiosInstance
        .put(`/vendors/${vendorId}/respond-to-ai-verification`, {
          aiStatus,
          aiVerificationNote,
          aiVerificationRetryDate,
        })
        .then(() => {
          resolve({ vendorId, aiStatus })
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  },
)

export const getAIResults = createAsyncThunk(
  'vendor/getAIResults',
  async (vendorId: string): Promise<AIResult[]> => {
    return await new Promise((resolve, reject) => {
      axios
        .get<AIResult[]>(`/ai-questionnaire/vendors/${vendorId}/results`)
        .then((data) => {
          resolve(data)
        })
        .catch((err) => {
          console.error('err: ', err)
          reject(err)
        })
    })
  },
)
